import { NgModel } from '@angular/forms';
import {
	Directive,
	ElementRef,
	Input,
	HostListener,
	Output,
	EventEmitter,
	OnInit,
} from '@angular/core';
import {
	isNegativeFloat,
	isPositiveFloat,
	isNegativeInteger,
	isPostiveInteger,
} from '../../util/number';
import { isNotNull } from '../../util/check';

/**
 * This Directive is used to allow user to enter only number in Input Field.
 * To use this directive import FnCoreModule in Module.
 */
@Directive({
	selector: '[fnNumberDirective]',
	providers: [NgModel],
})
export class FnNumberDirective implements OnInit {
	@Output() changeEvent = new EventEmitter<string>();
	@Output() ngModelChange = new EventEmitter<any>();
	/**
	 * Input Member variable
	 * if true then allow negtaive number else allow only positive number.
	 */
	@Input() isNegative: boolean;
	/**
	 * Input  Member variable
	 * if true then allow decimal number else allow only Integer Number.
	 */
	@Input() isDecimal: boolean;
	/**
	 * Input  Member variable
	 * used to restrict max characters to input
	 */
	@Input() maxLength: number;
	/**
	 * Input  Member variable
	 * used to validate max char before decimal
	 */
	@Input() beforeDecimal: number;
	/**
	 * Input  Member variable
	 * used to validate max char after decimal
	 */
	@Input() afterDecimal: number;
	/**
	 * Input  Member variable
	 * used to validate number if true then minus is not included in Length
	 */
	@Input() isNotIncludeMinus: boolean;
	/**
	 * Input member.
	 * used to allow zero at the start of digit.
	 */
	@Input() preZero = false;
	/**
	 * private member variable
	 * used to store the underlying native element to focus element on drop event and also to get the value of element.
	 */
	public nativeElement: any;
	/**
	 * private Obj to store required data in number directive
	 */
	private numberMap = {
		selectedVal: '',
		oldValue: '',
		startCursorposition: 0,
		endCursorposition: 0,
	};

	/**
	 * Creates an instance of fn number directive.
	 * @param model reference of FormControl
	 * @param elementRef refernce of ElementRef to get nativeElement.
	 */
	constructor(protected model: NgModel, protected elementRef: ElementRef) {
		this.nativeElement = this.elementRef.nativeElement;
	}

	/**
	 * member method
	 * store selected value to replace in input field in selectedVal variable.
	 */
	removeSelectedVal() {
		this.numberMap.selectedVal = window.getSelection().toString();
	}
	@HostListener('keydown', ['$event'])
	onKeyDown(event: any) {
		this.numberMap.startCursorposition = event.target.selectionStart;
		this.numberMap.endCursorposition = event.target.selectionEnd;
	}
	/**
	 * Hosts listener to reset input if invaild
	 * @param event any
	 * @returns null
	 */
	@HostListener('input', ['$event'])
	onInput(event: any) {
		const value = event.target.value || '';
		const oldValue = this.numberMap.oldValue;
		if (oldValue !== value && value.length > 0 && !this.isValidString(value)) {
			this.ngModelChange.emit(oldValue);
			this.model.valueAccessor.writeValue(oldValue);
			this.nativeElement.setSelectionRange(
				this.numberMap.startCursorposition,
				this.numberMap.endCursorposition
			);
			return;
		}
		this.numberMap.oldValue = value;
	}
	/**
	 * Determines input number is not valid of length
	 * @param value string of number
	 * @returns true if value is greater than maxLength
	 */
	isNotValidLength(value: string) {
		if (isNotNull(this.isNotIncludeMinus) && this.isNotIncludeMinus) {
			value = value.replace('-', '');
		}
		if (this.isDecimal) {
			const numArray = value.split('.');
			if (
				isNotNull(this.beforeDecimal) &&
				numArray[0].length > this.beforeDecimal
			) {
				return true;
			}
			const valueAfterDot = numArray[1];
			if (
				isNotNull(this.afterDecimal) &&
				isNotNull(valueAfterDot) &&
				valueAfterDot.length > this.afterDecimal
			) {
				return true;
			}
		}
		return value.length > this.maxLength;
	}

	/**
	 * Hosts listener listen for paste Event on input field.
	 * replace selected value from input field or insert value in input field by pasting number, if only pasted value is valid
	 * @param event Of type ClipboardEvent
	 */
	@HostListener('paste', ['$event'])
	onPaste(event: ClipboardEvent) {

		const pastedInput: string = event.clipboardData.getData('text/plain');
		this.executeInsertText(pastedInput, event);
	}

	/**
	 * Hosts listener listen for paste Event on input field.
	 * Allow To drop text only if text is valid number.
	 * @param event of Type DragEvent
	 */
	@HostListener('drop', ['$event'])
	onDrop(event: DragEvent) {
		const textData = event.dataTransfer.getData('text');
		this.nativeElement.focus();
		this.executeInsertText(textData, event);
	}

	/**
	 * method to insert Text in input field if inserted text is valid.
	 * @param textData text to insert in input field.
	 * @param event to get cursor postion.
	 */
	executeInsertText(textData:any, event:any) {
		this.removeSelectedVal();
		if (this.isValidPastString(textData, event)) {
			document.execCommand('insertText', false, textData);
		} else {
			document.execCommand('insertText', false, this.numberMap.selectedVal);
		}
		event.preventDefault();
	}
	/**
	 * Member method check input key is digit.
	 * @param eventKey of type string
	 * @returns true if key is number and not entering text before minus.
	 */
	isDigit(eventKey: string) {
		return eventKey >= '0' && eventKey <= '9';
	}

	/**
	 *  Member method to check all text input field is selected or not.
	 * @returns true if all text is selected in input field.
	 */
	isAllTextSelected() {
		this.removeSelectedVal();
		return (
			this.nativeElement.value.trim().length ===
			this.numberMap.selectedVal.length
		);
	}
	/**
	 * Member method check the pasted or dorped value is valid or not.
	 * @param inputVal of Type string
	 * @param event of Type any
	 * @returns true if valid past string
	 */
	isValidPastString(inputVal: string, event: any): boolean {
		return this.isValidString(this.valueAppendWithPasteString(inputVal, event));
	}
	/**
	 * Determines whether inputed text is valid
	 * @param value inputed text
	 * @returns  true if valid
	 */
	isValidString(value: string) {
		return !this.isNotValidLength(value) && this.isAllowPastingOrDroping(value);
	}
	/**
	 * Member method return new value append to input value
	 * @param inputVal of Type string
	 * @param event of Type any
	 * @returns appended string
	 */
	valueAppendWithPasteString(inputVal: string, event: any) {
		const startCursorposition = event.target.selectionStart;
		const endCursorposition = event.target.selectionEnd;
		return this.replaceBetween(
			startCursorposition,
			endCursorposition,
			inputVal
		);
	}

	/**
	 * Member method check the input is valid integer Number or not.
	 * @param inputVal of Type string
	 * @returns true if valid number
	 */
	isValidInteger(inputVal: string): boolean {
		return this.isNegative
			? inputVal === '-' || isNegativeInteger(inputVal)
			: isPostiveInteger(inputVal);
	}

	/**
	 * Member method check the input is valid Float Number or not.
	 * @param inputVal of Type string
	 * @returns true if valid decimal number
	 */
	isValidFloat(inputVal: string): boolean {
		if (this.isNegative) {
			if (inputVal.startsWith('-.')) {
				inputVal = inputVal.replace('.', '0.');
			}
			return inputVal === '-' || isNegativeFloat(inputVal);
		}
		return isPositiveFloat(inputVal);
	}
	/**
	 * Member method to replace String by inputValue by give start and Intex.
	 * @param start Type of number for Start Index.
	 * @param end Type of number for end Index.
	 * @param inputVal Type of String used to append.
	 * @returns string value after replaced by inputValue.
	 */
	replaceBetween(start: number, end: number, inputVal: string): string {
		const value = this.nativeElement.value;
		return value !== undefined && value !== ''
			? value.substring(0, start) + inputVal + value.substring(end)
			: inputVal;
	}

	/**
	 * Member method to check pasting or Dropping text is to allowed or not by validating value.
	 * @param val pasted or dropped text
	 * @returns true if value is Valid.
	 */
	isAllowPastingOrDroping(val: string): boolean {
		return this.isDecimal
			? val === '.' || this.isValidFloat(val)
			: this.isValidInteger(val);
	}
	ngOnInit() {
		setTimeout(() => {
			this.onBlur(
				this.elementRef.nativeElement.getAttribute('ng-reflect-model')
			);
		});
	}
	/**
	 * Hostlistener for input value change
	 * used to show valid value after input value change
	 * @param value  string
	 */
	@HostListener('change', ['$event.target.value'])
	onChange(value: string) {
		// tslint:disable-next-line: no-string-literal
		if (isNotNull(value)) {
			if (this.isDecimal) {
				if (value.indexOf('.') === value.length - 1) {
					value = value.replace('.', '');
				}
			}
			if (this.isNegative) {
				if (value.indexOf('-') === 0 && value.length === 1) {
					value = value.replace('-', '');
				}
			}
			value = (value !== '' && !this.preZero) ? parseFloat(value) + '' : (value !== '' && this.preZero) ? value : '';
		}
		this.ngModelChange.emit(value);
		this.changeEvent.emit(value);
	}
	/**
	 * Hosts listener to append value after dot on blur
	 * @param value input text
	 */
	@HostListener('blur', ['$event.target.value'])
	onBlur(value: any) {
		if (
			this.isDecimal &&
			this.model.valid &&
			isNotNull(value) &&
			value !== '' && !this.preZero
		) {
			this.model.valueAccessor.writeValue(
				String(parseFloat(value).toFixed(this.afterDecimal))
			);
		} else if (
			this.isDecimal &&
			this.model.valid &&
			isNotNull(value) &&
			value !== '' && this.preZero
		) {
			this.model.valueAccessor.writeValue(value);
		}
	}
}
